import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import actions from "@/actions";
import storageManager from "@/lib/storageManager";
import { RootState } from "@/reducers/types";
import { Button } from "@/sx-layout/common/Button";
import { Toggle } from "@/sx-layout/common/Toggle";
import FileExport from "@/sx-layout/components/plotmap/components/fileExport";
import { AddKeepOutAreaButton } from "@/sx-layout/components/plotmap/components/toolbar/components/AddKeepOutAreaButton";
import { PlotPlan, PlotPlanType } from "@/sx-layout/components/settings/plotPlanSetting/models";
import { useAuthorization, useTerm } from "@/sx-layout/hooks";
import { PlotPlanMapping } from "@/sx-layout/hooks/usePlotPlanMappingApi";
import { Role } from "@/sx-layout/models";

type Props = {
  canEditMap: number;
  disabled: boolean;
  plotPlan?: PlotPlan;
  overviewPlotPlanId?: number;
  clipAreas?: PlotPlanMapping[];
  loggedInUserCompanyId: number | undefined;
  onChangePlotPlan: () => void;
  onCreateMachine: () => void;
  onCreateArea: () => void;
  onCreateCustomArea: () => void;
  onCreateMark: () => void;
  onChangeEditable: (_) => void;
  onStartDrawingArrow: () => void;
  onAddText: () => void;
  onReload: () => void;
  scale: number;
  mapRatio: number;
};

export const Toolbar = forwardRef<HTMLDivElement, Props>(
  (
    {
      canEditMap,
      disabled,
      plotPlan,
      overviewPlotPlanId,
      clipAreas,
      loggedInUserCompanyId,
      onChangePlotPlan,
      onCreateMachine,
      onCreateArea,
      onCreateCustomArea,
      onCreateMark,
      onChangeEditable,
      onStartDrawingArrow,
      onAddText,
      onReload,
      scale,
      mapRatio,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const layoutDate = useSelector<RootState, Date>((state) => state.plotmap.layoutDate);
    const { isPast } = useTerm(layoutDate);
    const { isSpectator } = useAuthorization();

    const layoutRoles = useSelector<RootState, Role>((state) => state.app.roles.layout?.layout);
    const overviewPlotPlanIdAssumed = useMemo(() => {
      if (overviewPlotPlanId) return undefined;
      if (!plotPlan?.plot_plan_id || plotPlan.plot_plan_type !== PlotPlanType.DETAIL) return undefined;
      return clipAreas?.[0]?.overview_plot_plan_id;
    }, [overviewPlotPlanId, plotPlan?.plot_plan_id, plotPlan?.plot_plan_type, clipAreas]);

    const backToPlotPlanOverview = () => {
      const plotPlanId = overviewPlotPlanId ?? overviewPlotPlanIdAssumed;
      dispatch(actions.plotmap.selectPlotPlan(String(plotPlanId)));
      storageManager.setPlotPlanId(plotPlanId);
    };

    return (
      <div ref={ref} className="flex items-center justify-between whitespace-nowrap">
        <div className="flex items-center space-x-6 tbl-top-area">
          <h2 className="my-8 font-bold text-[16px]">{t("deploy_heavy_machine")}</h2>
          <span className="underline text-[13px] cursor-pointer" onClick={onChangePlotPlan}>
            {plotPlan?.plot_plan_name ?? t("unselected")}
          </span>
          <div className="flex items-center space-x-3">
            {plotPlan?.plot_plan_type !== PlotPlanType.OVERVIEW && (
              <>
                <Button
                  disabled={disabled || !plotPlan}
                  className={`${layoutRoles?.machine_create ? "" : "hidden"} flex items-center justify-center`}
                  compact={true}
                  onClick={onCreateMachine}
                >
                  <img src="./img/icon-add.svg" className="mr-2 w-[16px]" />
                  {t("heavy_machine")}
                </Button>
                <AddKeepOutAreaButton
                  onCreateArea={onCreateArea}
                  onCreateCustomArea={onCreateCustomArea}
                  disabled={disabled}
                />
                <Button
                  disabled={disabled || !plotPlan}
                  className={`${layoutRoles?.machine_create ? "" : "hidden"} flex items-center justify-center`}
                  compact={true}
                  onClick={onCreateMark}
                >
                  <img src="./img/icon-add.svg" className="mr-2 w-[16px]" />
                  {t("mark")}
                </Button>
              </>
            )}
            <Button
              id="arrow-add-button"
              disabled={isPast || isSpectator || disabled}
              className="min-w-[30px] flex items-center justify-center"
              compact={true}
              onClick={onStartDrawingArrow}
            >
              <img src="./img/icon-arrow-white.svg" className="mr-2 w-[18px]" />
              <p className="">{t("arrow")}</p>
            </Button>
            <Button
              disabled={isPast || isSpectator || disabled}
              className="min-w-[30px] flex items-center justify-center"
              compact={true}
              onClick={onAddText}
            >
              <img src="./img/icon-text-white.svg" className="mr-2 w-[18px]" />
              <p className="">{t("text")}</p>
            </Button>
            {plotPlan?.plot_plan_type !== PlotPlanType.OVERVIEW && (
              <>
                <FileExport
                  loggedInUserCompanyId={loggedInUserCompanyId}
                  scale={scale}
                  mapRatio={mapRatio}
                  compact={true}
                />
                {/* 全体プロットプランに戻るボタン */}
                {(overviewPlotPlanId || overviewPlotPlanIdAssumed) && (
                  <Button buttonType="cancel" compact={true} onClick={backToPlotPlanOverview}>
                    {t("back_to_plot_plan_overview")}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-6">
          <Toggle
            label={t("edit")}
            onChange={onChangeEditable}
            defaultChecked={canEditMap}
            className={`${layoutRoles?.update ? "" : "hidden"}`}
            isBold
          />
          <Button className="flex items-center justify-center" onClick={onReload}>
            <img src="./img/icon-reload.svg" className="mr-2 w-[18px]" />
            {t("reload")}
          </Button>
        </div>
      </div>
    );
  }
);

Toolbar.displayName = "Toolbar";
